<template>
  <row12>
    <row>
      <col10>
        <slot name="menu"></slot>
      </col10>
      <col2>
        <checkbox
          v-if="items.length > 0"
          data-testid="toggle-all-assets"
          v-model="toggleAllChecked"
          @toggled="toggleSelectAll"
          :label="$t('myassets.checkbox')"
        ></checkbox>
      </col2>
    </row>
    <row>
      <annotate-modal v-if="showModal && modalAssetId"
                      @remove_item="removeItem"
                      @close_modal="closeModal"
                      :asset_id="modalAssetId"
      ></annotate-modal>
      <col3 style=""
           class="results col-md-3"
           v-for="item in items" :key="item.id"
           :class="{ selected_grid_item: item.id===selected_id }"
      >
            <asset-grid-item
            :checked="item_ids_selected.includes(item.id)"
            :item_id="item.id"
            :image_url="item.path"
            :sensitive-blur="sensitiveBlurForAssetItem(item)"
            :annotations="item.annotations"
            :selected="item.id===selected_id"
            :in_validation_set="item.in_validation_set"
            :asset_validated="item.validated"
            :meta="item.meta"
            :meta-field-keys="assetGridMetaFieldKeys"
            @select_toggled="selectToggled"
            @remove_item="removeItem"
            @select_item="selectGridItem"
            :validation="item.validation"
        ></asset-grid-item>
      </col3>
    </row>
  </row12>
</template>

<script>
import AssetGridItem from "@/components/AssetGridItem";
import AnnotateModal from "@/components/AnnotateModal";
import Row from "@/components/layout/Row";
import Col10 from "@/components/layout/Col10";
import Col2 from "@/components/layout/Col2";
import Checkbox from "@/components/Checkbox";
import Row12 from "@/components/layout/Row12";
import Col3 from "@/components/layout/Col3";
import LoadSettingsMixin from "@/mixins/LoadSettingsMixin";
import CurrentUserMixin from "@/mixins/CurrentUserMixin.vue";
import {
  getBlurUnlessMetaFromSettings,
  shouldBlurAssetMeta,
} from '@/utils/sensitiveImageBlur';
import { getAssetGridMetaFieldsFromSettings } from '@/utils/assetGridMeta';
import { blockAnnotateModalOpen, canOpenAnnotateModal } from '@/utils/annotateModalGuard';

export default {
  props: {
    items: {required: true},
  },
  components: {
    Col3,
    Row12,
    Checkbox,
    Col2,
    Col10,
    Row,
    AnnotateModal,
    AssetGridItem
  },
  mixins: [LoadSettingsMixin, CurrentUserMixin],
  data() {
    return {
      selected_id: false,
      showModal: false,
      modalAssetId: false,
      modalDefaultOption: '',
      modalMeta: '',
      item_ids_selected: [],
      toggleAllChecked: false,
    }
  },
  computed: {
    selected_index() {
      return this.items.map(item=>{return item.id;}).indexOf(this.selected_id)
    },
    assetGridMetaFieldKeys() {
      return getAssetGridMetaFieldsFromSettings(this.settings || []);
    },
  },
  watch: {
    items() {
      // Grid remounts selection whenever results reload — keep toggle-all in sync
      // so the header checkbox does not stay checked with an empty selection.
      this.item_ids_selected = []
      this.toggleAllChecked = false
    },
    // Vue 3 does not deep-watch arrays by default; per-card toggles use
    // push/splice, so without deep (or replacing the array) the parent
    // never receives asset_ids_selected and menu actions become no-ops.
    item_ids_selected: {
      deep: true,
      handler(ids) {
        this.$emit('update_selection_asset_ids', ids);
      },
    },
  },
  methods: {
    sensitiveBlurForAssetItem(item) {
      const policy = getBlurUnlessMetaFromSettings(this.settings || []);
      return shouldBlurAssetMeta(item && item.meta, policy, this.is_sensitive_image_blur_exempt);
    },
    toggleSelectAll (value) {
      if(value) {
        let item_ids = this.items.map(image=>{return image.id});
        this.item_ids_selected = item_ids
      }
      else {
        this.item_ids_selected = []
      }
      this.$emit('update_selection_asset_ids', this.item_ids_selected);
    },
    addEvents: function(e) {
        let activeElement = document.activeElement;
        let inputs = ['input', 'select', 'button', 'textarea'];

        if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1) {
            return false;
        }

       if(!this.showModal && e.keyCode === 32) { //space
          e.stopPropagation();
          e.preventDefault();

          let index = this.selected_index
          let asset_id = this.items[index].id
          this.selectGridItem(asset_id)
        }

        if(this.showModal) {
            return;
        }

        if(e.keyCode === 13) { //enter
          e.stopPropagation();
          e.preventDefault();
          this.$emit('reload')
        }

        if(e.keyCode === 39) { //right arrow
          e.stopPropagation();
          e.preventDefault();
          let i = this.items.map( item => {return item.id;} ).indexOf(this.selected_id);
          let newI = i + 1
          if(i === -1 || newI > this.items.length -1) {
            newI = 0
          }
          this.selected_id = this.items[newI].id;
        }

        if(e.keyCode === 37) { //left arrow
          e.stopPropagation();
          e.preventDefault();
          let i = this.items.map( item => {return item.id;} ).indexOf(this.selected_id);
          let newI = i -1
          if(newI < 0) {
            newI = this.items.length -1 ;
          }
          this.selected_id = this.items[newI].id;
        }

        if(e.keyCode === 38) { //Up arrow
          e.stopPropagation();
          e.preventDefault();
          let i = this.items.map( item => {return item.id;} ).indexOf(this.selected_id);
          let newI = i - 4
          if(newI < 0) {
            return;
          }
          this.selected_id = this.items[newI].id;
        }

        if(e.keyCode === 40) { //Down arrow
          e.stopPropagation();
          e.preventDefault();
          let i = this.items.map( item => {return item.id;} ).indexOf(this.selected_id);
          let newI = i + 4
          if(newI > this.items.length - 1) {
            return;
          }
          this.selected_id = this.items[newI].id;
        }
      },
    closeModal() {
      blockAnnotateModalOpen();
      this.modalAssetId = null;
      this.showModal = false;
    },
    selectGridItem(asset_id) {
      if (!canOpenAnnotateModal()) {
        return;
      }
      if (!this.modalAssetId && !this.showModal) {
        // Defer so the grid click cannot hit the new dialog overlay and
        // immediately close the modal (same pattern as annotateModalGuard).
        window.setTimeout(() => {
          if (!canOpenAnnotateModal() || this.modalAssetId || this.showModal) {
            return;
          }
          this.modalAssetId = asset_id;
          this.showModal = true;
        }, 50);
      }
    },
    removeItem(asset_id) {
      this.$emit('remove_item', asset_id)
    },
    selectToggled(item_id, checked) {
      const next = [...this.item_ids_selected];
      const index = next.indexOf(item_id);
      if (checked === undefined) {
        if (index === -1) {
          next.push(item_id);
        } else {
          next.splice(index, 1);
        }
      } else if (checked) {
        if (index === -1) {
          next.push(item_id);
        }
      } else if (index !== -1) {
        next.splice(index, 1);
      }
      this.item_ids_selected = next;
      // Emit explicitly (same pattern as Grid.vue) so menu actions see the selection
      // even if a shallow watcher misses an in-place mutation.
      this.$emit('update_selection_asset_ids', next);
    },
    clearSelection() {
      this.item_ids_selected = [];
      this.toggleAllChecked = false;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.addEvents);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.addEvents);
  },
}
</script>

<style scoped>

</style>